"use client"

import React, {useEffect} from "react";
import {Button} from "@heroui/button";
import {Input} from "@heroui/input";
import {Link} from "@heroui/link";
import {ModalBody, ModalFooter, ModalHeader} from "@heroui/modal";
import {useQuery} from "@tanstack/react-query";
import {useMonitoring} from "@/components/monitoring";
import {isDev} from "@/components/DownloaderApp/utils";
import {getLicenseExpiredMessage} from "@/utils/license-expiration";
import {saveLicenseKeyToFile} from "@/utils/save-license-key-to-file";

type DownloadHistoryEntry = {
    url: string;
    timestamp: number | null;
    channel: string | null;
    platform: "kick" | "twitch" | null;
};

const EXAMPLE_TWITCH_DOWNLOAD: DownloadHistoryEntry = {
    url: "https://www.twitch.tv/videos/2857234947",
    timestamp: Date.now(),
    channel: "lyasyaa",
    platform: "twitch",
};

export type SubscriptionDetails = {
    email?: string;
    env?: string;
    status?: string;
    expiresAt?: string;
    platform?: string;
    vods?: Array<string | {
        uuid?: string;
        url?: string;
        downloadedAt?: string;
        channel?: string;
    }>;
};

type PremiumManagePopupProps = {
    data: {
        licenseKey: string | null;
        setLicenseKey: (licenseKey: string | null) => void;
        details?: SubscriptionDetails | null;
        isPreview?: boolean;
    };
    onClose: () => void;
    onAccept: () => void;
};

async function fetchSubscription(licenseKey: string): Promise<SubscriptionDetails> {
    const response = await fetch(`/api/subscription?license_key=${encodeURIComponent(licenseKey)}`);
    if (!response.ok) {
        throw new Error(`Could not load subscription: ${response.status}`);
    }
    return response.json();
}

function getDownloadHistory(details?: SubscriptionDetails | null): DownloadHistoryEntry[] {
    if (!Array.isArray(details?.vods)) return [];

    return details.vods
        .filter((entry): entry is {url: string; downloadedAt?: string; channel?: string} =>
            typeof entry === "object" && entry !== null && typeof entry.url === "string"
        )
        .map(entry => {
            const parsedTimestamp = entry.downloadedAt ? Date.parse(entry.downloadedAt) : NaN;
            return {
                url: entry.url,
                timestamp: Number.isFinite(parsedTimestamp) ? parsedTimestamp : null,
                channel: entry.channel || null,
                platform: details.platform === "kick" || details.platform === "twitch"
                    ? details.platform
                    : null,
            };
        })
        .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
}

function formatDownloadedAt(timestamp: number): string {
    const hoursAgo = Math.floor((Date.now() - timestamp) / (60 * 60 * 1000));

    if (hoursAgo < 1) return "Less than an hour ago";
    if (hoursAgo === 1) return "1 hour ago";
    if (hoursAgo >= 24) {
        const daysAgo = Math.floor(hoursAgo / 24);
        const remainingHours = hoursAgo % 24;
        const daysLabel = `${daysAgo} ${daysAgo === 1 ? "day" : "days"}`;

        if (remainingHours === 0) return `${daysLabel} ago`;
        return `${daysLabel} ${remainingHours} ${remainingHours === 1 ? "hour" : "hours"} ago`;
    }
    return `${hoursAgo} hours ago`;
}

function formatFullDownloadedAt(timestamp: number): string {
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: "full",
        timeStyle: "long",
    }).format(timestamp);
}

export function PremiumManagePopup({data, onClose, onAccept}: PremiumManagePopupProps) {
    const {track} = useMonitoring();
    const {setLicenseKey, licenseKey, details, isPreview = false} = data;
    const {
        data: subscription = details,
        isError: isHistoryError,
        isFetching: isHistoryLoading,
    } = useQuery({
        queryKey: [isPreview ? "subscription-preview" : "subscription", licenseKey, ...(isPreview ? [details] : [])],
        queryFn: () => {
            if (!licenseKey) throw new Error("A license key is required");
            return fetchSubscription(licenseKey);
        },
        enabled: !!licenseKey && !isPreview,
        initialData: details || undefined,
        refetchOnMount: isPreview ? false : "always",
    });
    const downloadHistory = [
        ...(!isPreview && isDev ? [EXAMPLE_TWITCH_DOWNLOAD] : []),
        ...getDownloadHistory(subscription),
    ];

    useEffect(() => {
        if (!isPreview) track("[Premium] Manage popup opened");
    }, [track, isPreview]);

    return (
        <>
            <ModalHeader className="flex flex-col gap-1">
                <span id="modal-title">Manage license</span>
            </ModalHeader>
            <ModalBody>
                {subscription?.status === "expired" && (
                    <p role="status" className="flex items-center gap-1 text-sm text-warning">
                        <span aria-hidden="true">⚠️</span>
                        {getLicenseExpiredMessage(subscription.expiresAt)}
                    </p>
                )}
                <div className="grid gap-6 md:grid-cols-2">
                    <div className="flex flex-col gap-5">
                        <Input
                            isDisabled
                            label="Your License Key:"
                            value={licenseKey || ""}
                            type="text"
                            variant="bordered"
                            size="md"
                            alt="License key"
                        />
                        <Button
                            onPress={onAccept}
                            isDisabled={isPreview}
                            color="primary"
                            className="w-full capitalize"
                        >
                            Get More Credits ({details?.platform || "premium"})
                        </Button>
                        <Button
                            className="w-full"
                            variant="bordered"
                            isDisabled={!licenseKey}
                            onPress={() => {
                                if (!licenseKey) throw new Error("A license key is required");
                                saveLicenseKeyToFile(licenseKey);
                                if (!isPreview) track("[Premium] License key saved to file", {licenseKey});
                            }}
                        >
                            Save License Key to File
                        </Button>
                        <Button
                            className="w-full"
                            onPress={() => {
                                setLicenseKey(null);
                                onClose();
                                if (!isPreview) track("[Premium] License removed", {licenseKey});
                            }}
                            size="sm"
                            color="danger"
                        >
                            Remove License
                        </Button>
                    </div>

                    <section aria-labelledby="download-history-title" className="min-w-0">
                        <div className="mb-2 flex items-center justify-between gap-3">
                            <h3 id="download-history-title" className="font-semibold">
                                Recent downloads
                            </h3>
                            <span className="text-xs text-default-500">
                                {downloadHistory.length} {downloadHistory.length === 1 ? "download" : "downloads"}
                            </span>
                        </div>

                        {isHistoryLoading && !subscription ? (
                            <p className="rounded-lg bg-default-100 px-4 py-5 text-center text-sm text-default-500">
                                Loading download history...
                            </p>
                        ) : isHistoryError ? (
                            <p className="rounded-lg bg-danger-50 px-4 py-5 text-center text-sm text-danger">
                                Download history could not be loaded.
                            </p>
                        ) : downloadHistory.length === 0 ? (
                            <p className="rounded-lg bg-default-100 px-4 py-5 text-center text-sm text-default-500">
                                No downloads recorded yet.
                            </p>
                        ) : (
                            <ol className="flex max-h-72 flex-col divide-y divide-default-200 overflow-y-auto rounded-lg border border-default-200">
                                {downloadHistory.map((entry, index) => (
                                    <li className="px-3 py-3" key={`${entry.timestamp}-${entry.url}-${index}`}>
                                        <Link
                                            className="block w-full min-w-0 text-sm"
                                            href={entry.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            title={entry.url}
                                        >
                                            {entry.url.length <= 50 ? entry.url : (
                                                <span className="flex w-full min-w-0">
                                                    <span className="min-w-0 overflow-hidden whitespace-nowrap">
                                                        {entry.url.slice(0, -5)}
                                                    </span>
                                                    <span className="shrink-0">...{entry.url.slice(-5)}</span>
                                                </span>
                                            )}
                                        </Link>
                                        {(entry.timestamp !== null || (entry.channel && entry.platform)) && (
                                            <div className="mt-1 flex items-center gap-1 text-xs text-default-500">
                                                {entry.timestamp !== null && (
                                                    <time
                                                        dateTime={new Date(entry.timestamp).toISOString()}
                                                        title={formatFullDownloadedAt(entry.timestamp)}
                                                    >
                                                        {formatDownloadedAt(entry.timestamp)}
                                                    </time>
                                                )}
                                                {entry.channel && entry.platform && (
                                                    <>
                                                        {entry.timestamp !== null && <span aria-hidden="true">·</span>}
                                                        <Link
                                                            className="text-xs"
                                                            href={entry.platform === "kick"
                                                                ? `/channel/${encodeURIComponent(entry.channel)}`
                                                                : `https://www.twitch.tv/${entry.channel}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            @{entry.channel}
                                                        </Link>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        )}
                    </section>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button onPress={onClose}>Close</Button>
            </ModalFooter>
        </>
    );
}
